import { useParams, Link } from 'react-router-dom';
import { useSettings } from '@/context/SettingsContext';

const PAGE_CONFIG = {
  impressum: { key: 'legal_impressum', title: 'Impressum' },
  datenschutz: { key: 'legal_datenschutz', title: 'Datenschutzerklärung' },
  agb: { key: 'legal_agb', title: 'Allgemeine Geschäftsbedingungen' },
};

// Convert plain text to formatted HTML
// - Double newlines become paragraphs
// - Single newlines become <br> tags
// - Preserves existing HTML if present (for backward compatibility)
const formatContent = (text) => {
  if (!text) return '';
  
  // Check if content already contains HTML tags (legacy content)
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(text);
  if (hasHtmlTags) {
    // For legacy HTML content, just return as-is
    return text;
  }
  
  // For plain text, convert to formatted HTML
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs
    .map(para => {
      // Convert single newlines to <br> within paragraphs
      const formattedPara = para.trim().replace(/\n/g, '<br>');
      return formattedPara ? `<p>${formattedPara}</p>` : '';
    })
    .filter(p => p)
    .join('');
};

export default function LegalPage() {
  const { page } = useParams();
  const { settings: s, settingsLoaded } = useSettings();

  const config = PAGE_CONFIG[page];

  if (!config) {
    return (
      <main className="min-h-screen pt-28 pb-16 px-4 max-w-4xl mx-auto" data-testid="legal-not-found">
        <h1 className="text-2xl font-bold text-[#0E518D] mb-4">Seite nicht gefunden</h1>
        <Link to="/" className="text-[#0E518D] underline">Zurück zur Startseite</Link>
      </main>
    );
  }

  const rawContent = s[config.key] || '';
  const formattedContent = formatContent(rawContent);

  return (
    <main className="min-h-screen pt-28 pb-16 px-4" data-testid={`legal-${page}`}>
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="text-sm text-[#0E518D] hover:underline mb-6 inline-block" data-testid="legal-back-link">
          ← Zurück zur Startseite
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0E518D] mb-8" data-testid="legal-title">{config.title}</h1>
        {!settingsLoaded ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" />
          </div>
        ) : formattedContent ? (
          <div
            className="prose prose-slate max-w-none text-slate-700 leading-relaxed prose-p:my-4 prose-headings:text-[#0E518D]"
            style={{ fontFamily: 'Arial, sans-serif' }}
            dangerouslySetInnerHTML={{ __html: formattedContent }}
            data-testid="legal-content"
          />
        ) : (
          <p className="text-slate-400 text-center py-12" data-testid="legal-empty">
            Inhalt wird in Kürze bereitgestellt.
          </p>
        )}
      </div>
    </main>
  );
}
